import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

/* ---- Mockup: Kalender-Wochenansicht (acquire) ---- */
const CalendarMockup: React.FC = () => {
  const { t } = useTranslation();
  const mockup = t('features.mockup', { returnObjects: true }) as any;
  const days = mockup.calendarDays as string[];
  const slotLabels = mockup.calendarSlots as string[];
  const slots: { day: number; time: string; label: string; color: string }[] = [
    { day: 0, time: '09:00', label: slotLabels[0], color: 'blue' },
    { day: 3, time: '09:00', label: slotLabels[1], color: 'green' },
    { day: 1, time: '11:00', label: slotLabels[2], color: 'amber' },
    { day: 4, time: '11:00', label: slotLabels[3], color: 'purple' },
    { day: 2, time: '14:00', label: slotLabels[4], color: 'blue' },
  ];

  return (
    <div className="hp-features-mockup-card">
      <div className="hp-mock-inv-header">
        <span className="hp-mock-inv-title">{mockup.calendarTitle}</span>
        <span className="hp-mock-inv-badge">{mockup.calendarBadge}</span>
      </div>
      <div className="hp-mock-week-grid">
        <span />
        {days.map(d => (
          <span key={d} className="hp-mock-week-header">{d}</span>
        ))}
        {['09:00', '11:00', '14:00'].map(time => (
          <React.Fragment key={time}>
            <span className="hp-mock-week-time">{time}</span>
            {days.map((_, di) => {
              const slot = slots.find(s => s.day === di && s.time === time);
              return (
                <div key={di} className="hp-mock-week-cell">
                  {slot && (
                    <div className={`hp-mock-week-event hp-mock-week-event--${slot.color}`}>
                      {slot.label}
                    </div>
                  )}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

/* ---- Mockup: Klient-Karte mit Prozessschritten (deliver) ---- */
const ClientProcessMockup: React.FC = () => {
  const { t } = useTranslation();
  const mockup = t('features.mockup', { returnObjects: true }) as any;
  const processSteps = mockup.processSteps as { label: string; date: string; statusText: string }[];

  const icons = ['💬', '📋', '🎯', '📚', '🔄', '✅'];
  const statuses = ['done', 'done', 'done', 'current', 'pending', 'pending'];
  const bgs = ['#dcfce7', '#dbeafe', '#fef9c3', '#ede9fe', '#f1f5f9', '#f1f5f9'];

  return (
    <div className="hp-features-mockup-card">
      <div className="hp-mock-client-header">
        <span className="hp-mock-client-name">{mockup.clientName}</span>
        <span className="hp-mock-client-badge">{mockup.clientBadge}</span>
      </div>
      <div className="hp-mock-process-steps">
        {processSteps.map((step, i) => (
          <div key={i} className={`hp-mock-process-step hp-mock-process-step--${statuses[i]}`}>
            <span className="hp-mock-step-icon" style={{ background: bgs[i] }}>{icons[i]}</span>
            <span className="hp-mock-step-name">{step.label}</span>
            <span className="hp-mock-step-date">{step.date}</span>
            <span className="hp-mock-step-status">{step.statusText}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ---- Mockup: Rechnungstabelle (manage) ---- */
const InvoiceMockup: React.FC = () => {
  const { t } = useTranslation();
  const mockup = t('features.mockup', { returnObjects: true }) as any;
  const invoiceTh = mockup.invoiceTh as string[];
  const invoiceRows = mockup.invoiceRows as { status: string }[];

  const rows = [
    { nr: 'RE-2026-0047', initials: 'MK', color: '#3b82f6', client: 'Maria K.', amount: '€ 1.200,00', cls: 'paid' },
    { nr: 'RE-2026-0048', initials: 'TS', color: '#8b5cf6', client: 'Thomas S.', amount: '€ 890,00', cls: 'sent' },
    { nr: 'RE-2026-0046', initials: 'AB', color: '#f59e0b', client: 'Anna B.', amount: '€ 450,00', cls: 'overdue' },
    { nr: 'AN-2026-0012', initials: 'LW', color: '#10b981', client: 'Lisa W.', amount: '€ 2.400,00', cls: 'draft' },
  ];

  return (
    <div className="hp-features-mockup-card">
      <div className="hp-mock-inv-header">
        <span className="hp-mock-inv-title">{mockup.invoiceTitle}</span>
        <span className="hp-mock-inv-badge" style={{ color: '#16a34a' }}>{mockup.invoiceQuarter}</span>
      </div>
      <table className="hp-mock-inv-table">
        <thead>
          <tr>
            {invoiceTh.map((th: string) => <th key={th}>{th}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.nr}>
              <td>{r.nr}</td>
              <td>
                <span className="hp-mock-inv-avatar" style={{ background: r.color }}>{r.initials}</span>
                {r.client}
              </td>
              <td style={{ fontWeight: 600 }}>{r.amount}</td>
              <td>
                <span className={`hp-mock-inv-status hp-mock-inv-status--${r.cls}`}>
                  {invoiceRows[i]?.status}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/* ---- Main Component ---- */
const FeaturesSection: React.FC = () => {
  const { t } = useTranslation();
  const headerRef = useScrollAnimation<HTMLDivElement>();
  const contentRef = useScrollAnimation<HTMLDivElement>(0.1);
  const [activeTab, setActiveTab] = useState(0);
  const [paused, setPaused] = useState(false);
  const [progressKey, setProgressKey] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const tabs = t('features.tabs', { returnObjects: true }) as {
    label: string; title: string; description: string; outcomes: string[];
  }[];

  const mockups = [<CalendarMockup />, <ClientProcessMockup />, <InvoiceMockup />];
  const tabIds = ['acquire', 'deliver', 'manage'];

  const goToTab = useCallback((idx: number) => {
    setActiveTab(idx);
    setProgressKey(k => k + 1);
  }, []);

  const nextTab = useCallback(() => {
    setActiveTab(prev => (prev + 1) % tabs.length);
    setProgressKey(k => k + 1);
  }, [tabs.length]);

  useEffect(() => {
    if (paused) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }
    intervalRef.current = setInterval(nextTab, 6000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [paused, nextTab]);

  const handleTabClick = (idx: number) => {
    goToTab(idx);
    setPaused(false);
  };

  const handleSmoothScroll = (e: React.MouseEvent) => {
    e.preventDefault();
    document.querySelector('#platform')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="features" className="hp-features" aria-labelledby="features-title">
      <div className="hp-features-inner">
        <div ref={headerRef} className="hp-section-header hp-animate">
          <span className="hp-section-label">{t('features.sectionLabel')}</span>
          <h2 id="features-title">{t('features.title')}</h2>
          <p>{t('features.subtitle')}</p>
        </div>

        <div
          ref={contentRef}
          className="hp-animate"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          {/* Tab Buttons */}
          <div className="hp-features-tabs" role="tablist">
            {tabs.map((tab, i) => (
              <button
                key={tabIds[i]}
                role="tab"
                aria-selected={i === activeTab}
                aria-controls={`panel-${tabIds[i]}`}
                className={`hp-features-tab${i === activeTab ? ' hp-features-tab--active' : ''}`}
                onClick={() => handleTabClick(i)}
              >
                {tab.label}
                <span
                  key={i === activeTab ? progressKey : 'inactive'}
                  className="hp-features-tab-progress"
                />
              </button>
            ))}
          </div>

          {/* Tab Panels */}
          {tabs.map((tab, i) => (
            <div
              key={tabIds[i]}
              id={`panel-${tabIds[i]}`}
              role="tabpanel"
              className={`hp-features-panel${i === activeTab ? ' hp-features-panel--active' : ''}`}
            >
              <div className="hp-features-panel-text">
                <h3>{tab.title}</h3>
                <p>{tab.description}</p>
                <ul className="hp-features-outcomes">
                  {tab.outcomes.map((o, j) => (
                    <li key={j}>
                      <span className="hp-features-outcome-icon">✓</span>
                      {o}
                    </li>
                  ))}
                </ul>
                <button className="hp-features-cta" onClick={handleSmoothScroll}>
                  {t('features.learnMore')}
                </button>
              </div>
              <div className="hp-features-mockup-wrap">
                {mockups[i]}
              </div>
            </div>
          ))}

          {/* Dots (mobile only) */}
          <div className="hp-features-dots">
            {tabs.map((_, i) => (
              <button
                key={i}
                className={`hp-features-dot${i === activeTab ? ' hp-features-dot--active' : ''}`}
                onClick={() => handleTabClick(i)}
                aria-label={`Tab ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
